import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { AuthCallback } from "@/components/AuthCallback";
import { ProtectedRoute, AdminRoute } from "@/components/RouteGuards";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { AdminLayout } from "@/components/layout/AdminLayout";

import Home from "@/pages/public/Home";
import Vip from "@/pages/public/Vip";
import Brokers from "@/pages/public/Brokers";
import Education from "@/pages/public/Education";
import EducationArticle from "@/pages/public/EducationArticle";
import Results from "@/pages/public/Results";
import About from "@/pages/public/About";
import Offers from "@/pages/public/Offers";
import Faq from "@/pages/public/Faq";
import Contact from "@/pages/public/Contact";
import Legal from "@/pages/public/Legal";

import Login from "@/pages/auth/Login";
import Signup from "@/pages/auth/Signup";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";

import Dashboard from "@/pages/dashboard/Dashboard";
import Profile from "@/pages/dashboard/Profile";
import VipAccess from "@/pages/dashboard/VipAccess";
import BrokerVerification from "@/pages/dashboard/BrokerVerification";
import Referrals from "@/pages/dashboard/Referrals";
import Tickets from "@/pages/dashboard/Tickets";
import TicketDetail from "@/pages/dashboard/TicketDetail";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminVip from "@/pages/admin/AdminVip";
import AdminReferrals from "@/pages/admin/AdminReferrals";
import AdminBrokers from "@/pages/admin/AdminBrokers";
import AdminVerifications from "@/pages/admin/AdminVerifications";
import AdminResults from "@/pages/admin/AdminResults";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import AdminEducation from "@/pages/admin/AdminEducation";
import AdminOffers from "@/pages/admin/AdminOffers";
import AdminFaq from "@/pages/admin/AdminFaq";
import AdminContent from "@/pages/admin/AdminContent";
import AdminLegal from "@/pages/admin/AdminLegal";
import AdminTickets from "@/pages/admin/AdminTickets";

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/vip" element={<Vip />} />
        <Route path="/brokers" element={<Brokers />} />
        <Route path="/education" element={<Education />} />
        <Route path="/education/:slug" element={<EducationArticle />} />
        <Route path="/results" element={<Results />} />
        <Route path="/about" element={<About />} />
        <Route path="/offers" element={<Offers />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/legal/:type" element={<Legal />} />
        <Route path="/privacy" element={<Legal />} />
        <Route path="/terms" element={<Legal />} />
        <Route path="/risk-disclosure" element={<Legal />} />
        <Route path="/disclaimer" element={<Legal />} />
      </Route>

      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/dashboard/profile" element={<Profile />} />
        <Route path="/dashboard/vip-access" element={<VipAccess />} />
        <Route path="/dashboard/broker-verification" element={<BrokerVerification />} />
        <Route path="/dashboard/referrals" element={<Referrals />} />
        <Route path="/dashboard/tickets" element={<Tickets />} />
        <Route path="/dashboard/tickets/:id" element={<TicketDetail />} />
      </Route>

      <Route element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/vip" element={<AdminVip />} />
        <Route path="/admin/referrals" element={<AdminReferrals />} />
        <Route path="/admin/brokers" element={<AdminBrokers />} />
        <Route path="/admin/verifications" element={<AdminVerifications />} />
        <Route path="/admin/results" element={<AdminResults />} />
        <Route path="/admin/testimonials" element={<AdminTestimonials />} />
        <Route path="/admin/education" element={<AdminEducation />} />
        <Route path="/admin/offers" element={<AdminOffers />} />
        <Route path="/admin/faq" element={<AdminFaq />} />
        <Route path="/admin/content" element={<AdminContent />} />
        <Route path="/admin/legal" element={<AdminLegal />} />
        <Route path="/admin/tickets" element={<AdminTickets />} />
      </Route>
    </Routes>
  );
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    // Keep native behavior for Back/Forward; let same-page anchor links scroll to their target.
    if (navType === "POP" || hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash, navType]);
  return null;
}

function ThemedToaster() {
  const { resolved } = useTheme();
  return <Toaster theme={resolved} position="top-right" richColors />;
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AppRouter />
            <ThemedToaster />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
